'use client'

import React, { createContext, useContext, useState, useCallback } from 'react'
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react'

interface Toast {
  id: string
  title: string
  description?: string
  variant?: 'default' | 'destructive' | 'success' | 'info'
  icon?: React.ReactNode
  duration?: number
}

interface ToastContextType {
  toasts: Toast[]
  toast: (toast: Omit<Toast, 'id'>) => void
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const toast = useCallback((toastData: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9)
    const duration = toastData.duration || 5000

    setToasts(prev => [...prev, { ...toastData, id }])

    // Auto-dismiss after duration
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, duration)
  }, [])

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, toast, dismiss }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

const ToastContainer: React.FC<{ toasts: Toast[], onDismiss: (id: string) => void }> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 max-w-[400px] w-full pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

const ToastItem: React.FC<{ toast: Toast, onDismiss: (id: string) => void }> = ({ toast, onDismiss }) => {
  const isDestructive = toast.variant === 'destructive'
  const isSuccess = toast.variant === 'success'

  const getIcon = () => {
    if (toast.icon) return toast.icon
    if (isDestructive) return <AlertCircle className="h-5 w-5 text-rose-500" />
    if (isSuccess) return <CheckCircle2 className="h-5 w-5 text-emerald-500" />
    return <Info className="h-5 w-5 text-[#21728d]" />
  }

  return (
    <div
      className={`
        pointer-events-auto relative group overflow-hidden
        rounded-2xl border backdrop-blur-md shadow-2xl p-4
        flex gap-3 items-start transition-all duration-500 animate-in slide-in-from-right-full
        ${isDestructive 
          ? 'bg-rose-50/90 border-rose-200/50 dark:bg-rose-950/20 dark:border-rose-800/30' 
          : 'bg-white/80 border-slate-200/50 dark:bg-slate-900/80 dark:border-slate-800/50'}
      `}
      onClick={() => onDismiss(toast.id)}
    >
      {/* baby: Genie-style accent bar */}
      <div 
        className={`absolute left-0 top-0 bottom-0 w-1.5 
          ${isDestructive ? 'bg-rose-500' : isSuccess ? 'bg-emerald-500' : 'bg-[#21728d]'}`} 
      />

      <div className="flex-shrink-0 mt-0.5">
        {getIcon()}
      </div>

      <div className="flex-1 min-w-0">
        <h4 className={`text-sm font-bold tracking-tight
          ${isDestructive ? 'text-rose-900 dark:text-rose-100' : 'text-slate-900 dark:text-slate-100'}`}
        >
          {toast.title}
        </h4>
        {toast.description && (
          <p className={`text-xs mt-1 leading-relaxed
            ${isDestructive ? 'text-rose-700 dark:text-rose-300' : 'text-slate-600 dark:text-slate-400'}`}
          >
            {toast.description}
          </p>
        )}
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation()
          onDismiss(toast.id)
        }}
        className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
} 